import axios from 'axios';
import { AppConfig } from '../models/app-config';
import { CDCCVX } from '../models/cdc-cvx';
import { CompositeAllergy } from '../models/composite-allergy';
import { CompositeDrug } from '../models/composite-drug';
import { CounselingType } from '../models/counseling-type';
import { DosageFormType } from '../models/dosage-form-type';
import { ICD10 } from '../models/icd10';
import { ImagingFacility } from '../models/imaging-facility';
import { ItemModel } from '../models/item';
import { Lab } from '../models/lab';
import { LabCorpEdifecsStandardCompendium } from '../models/lab-corp-edifecs-standard-compendium';
import { LabTestCode } from '../models/lab-test-code';
import { NLMPatientEducationDefinition } from '../models/nlm-patient-education-definition';
import { ProcedureType } from '../models/procedure-type';
import { QuestOrderCode } from '../models/quest-order-code';
import { Snomed } from '../models/snomed';
import { SnomedCategory } from '../models/snomed-category';
import { SpecialtyModel } from '../models/specialty';

const locked = {
    getSpecialties: false,
    getItems: false,
    getSnomedCategories: false,
    getDosageFormTypes: false,
    getCounselingTypes: false,
    getImagingFacilities: false,
    getLabs: false,
    getProcedureTypes: false,
    getCDCCVXs: false,
    getLabTestCodes: false
};

export const getAppConfig = async (): Promise<Undefinable<AppConfig>> => {
    const response = await axios.get(`/api/emr-data/app-config`);
    return response.data;
};

export const getSpecialties = async (): Promise<Undefinable<SpecialtyModel[]>> => {
    if (locked.getSpecialties) {
        return [];
    } else {
        locked.getSpecialties = true;
        const response = await axios.get(`/api/emr-data/specialty`);
        locked.getSpecialties = false;
        return response.data;
    }
};

export const getItems = async (): Promise<Undefinable<ItemModel[]>> => {
    if (locked.getItems) {
        return [];
    } else {
        locked.getItems = true;
        const response = await axios.get(`/api/emr-data/item`);
        locked.getItems = false;
        return response.data;
    }
};

export const getCompositeAllergies = async (
    allergyIds: number[]
): Promise<CompositeAllergy[]> => {
    const response = await axios.post(
        '/api/emr-data/composite-allergy',
        Array.from(new Set(allergyIds))
    );
    return response.data;
};

export const searchCompositeAllergies = async (text: string): Promise<Undefinable<CompositeAllergy[]>> => {
    try {
        const response = await axios.get(`/api/emr-data/composite-allergy/search?text=${text}`);
        return response.data;
    } catch (e) {
        window.console.log(e);
        return [];
    }
};

export const getSnomeds = async (conceptIds: number[], active: boolean): Promise<Undefinable<Snomed[]>> => {
    const action = active ? 'active-snomed' : 'snomed';
    const response = await axios.post(`/api/emr-data/${action}`, conceptIds);
    return response.data;
};

export const searchSnomeds = async (text: string): Promise<Undefinable<Snomed[]>> => {
    try {
        const response = await axios.get(`/api/emr-data/snomed/search?text=${text}`);
        return response.data;
    } catch (e) {
        window.console.log(e);
        return [];
    }
};

export const getSnomedCategories = async (): Promise<Undefinable<SnomedCategory[]>> => {
    if (locked.getSnomedCategories) {
        return [];
    } else {
        locked.getSnomedCategories = true;
        const response = await axios.get(`/api/emr-data/snomed-category`);
        locked.getSnomedCategories = false;
        return response.data;
    }
};

export const getCompositeDrugs = async (
    medIds: number[]
): Promise<CompositeDrug[]> => {
    const response = await axios.post(
        '/api/emr-data/composite-drug',
        Array.from(new Set(medIds))
    );
    return response.data;
};

export const searchCompositeDrugs = async (text: string): Promise<Undefinable<CompositeDrug[]>> => {
    try {
        const response = await axios.get(`/api/emr-data/composite-drug/search?text=${text}`);
        return response.data;
    } catch (e) {
        window.console.log(e);
        return [];
    }
};

export const getDosageFormTypes = async (): Promise<Undefinable<DosageFormType[]>> => {
    if (locked.getDosageFormTypes) {
        return [];
    } else {
        locked.getDosageFormTypes = true;
        const response = await axios.get(`/api/emr-data/dosage-form-type`);
        locked.getDosageFormTypes = false;
        return response.data;
    }
};

export const getICD10s = async (
    codes: string[]
): Promise<Undefinable<ICD10[]>> => {
    const response = await axios.post(
        '/api/emr-data/icd10',
        Array.from(new Set(codes))
    );
    return response.data;
};

export const searchICD10s = async (text: string): Promise<Undefinable<ICD10[]>> => {
    try {
        const response = await axios.get(`/api/emr-data/icd10/search?text=${text}`);
        return response.data;
    } catch (e) {
        window.console.log(e);
        return [];
    }
};

export const getNLMPatientEducationDefinitions = async (ids: number[]): Promise<Undefinable<NLMPatientEducationDefinition[]>> => {
    const response = await axios.post(`/api/emr-data/nlm-patient-education-definition`, ids);
    return response.data;
};

export const getCounselingTypes = async (): Promise<Undefinable<CounselingType[]>> => {
    if (locked.getCounselingTypes) {
        return [];
    } else {
        locked.getCounselingTypes = true;
        const response = await axios.get(`/api/emr-data/counseling-type`);
        locked.getCounselingTypes = false;
        return response.data;
    }
};

export const getImagingFacilities = async (): Promise<Undefinable<ImagingFacility[]>> => {
    if (locked.getImagingFacilities) {
        return [];
    } else {
        locked.getImagingFacilities = true;
        const response = await axios.get(`/api/emr-data/imaging-facility`);
        locked.getImagingFacilities = false;
        return response.data;
    }
};

export const getLabs = async (): Promise<Undefinable<Lab[]>> => {
    if (locked.getLabs) {
        return [];
    } else {
        locked.getLabs = true;
        const response = await axios.get(`/api/emr-data/lab`);
        locked.getLabs = false;
        return response.data;
    }
};

export const getProcedureTypes = async (): Promise<Undefinable<ProcedureType[]>> => {
    if (locked.getProcedureTypes) {
        return [];
    } else {
        locked.getProcedureTypes = true;
        const response = await axios.get(`/api/emr-data/procedure-type`);
        locked.getProcedureTypes = false;
        return response.data;
    }
};

export const getCDCCVXs = async (): Promise<Undefinable<CDCCVX[]>> => {
    if (locked.getCDCCVXs) {
        return [];
    } else {
        locked.getCDCCVXs = true;
        const response = await axios.get(`/api/emr-data/cdc-cvx`);
        locked.getCDCCVXs = false;
        return response.data;
    }
};

export const getLabTestCodes = async (): Promise<Undefinable<LabTestCode[]>> => {
    if (locked.getLabTestCodes) {
        return [];
    } else {
        locked.getLabTestCodes = true;
        const response = await axios.get(`/api/emr-data/lab-test-code`);
        locked.getLabTestCodes = false;
        return response.data;
    }
};

export const getQuestOrderCodes = async (testCDs: string[]): Promise<Undefinable<QuestOrderCode[]>> => {
    const response = await axios.post(`/api/emr-data/quest-order-code`, testCDs);
    return response.data;
};

export const getLabCorpEdifecsStandardCompendiums = async (orderCode: string[]): Promise<Undefinable<LabCorpEdifecsStandardCompendium[]>> => {
    const response = await axios.post(`/api/emr-data/lab-corp-edifecs-standard-compendium`, orderCode);
    return response.data;
};
